/**
 * Dietary accommodation notes — Settings > My School.
 *
 * The school office receives a note about a student's dietary restriction and
 * records it here. Submitting creates a PENDING_REVIEW accommodation that the
 * Ordo menus team reviews and links to a restricted menu; nothing about the
 * student's ordering changes until that happens.
 *
 * Intake is school-side only by design — parents never upload. The parent-side
 * surface is read-only status.
 */
import { specialDietStudentLabel } from '@ordo/constants/special-diet-student-label'
import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { EmptyState } from '@ordo/ui/empty-state'
import { Skeleton } from '@ordo/ui/skeleton'
import { FileText, Plus } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import { type AdminSchool } from '@/api/school-orders'
import {
  accommodationStatusCopy,
  useNoteOptionsQuery,
  useSchoolAccommodationsQuery,
  useSubmitAccommodationMutation,
} from '@/api/special-diet-accommodations'

import { AddNoteDialog } from './special-diet-intake-dialog'
export { buildRestrictionOptions, CUSTOM_RESTRICTION, splitRestrictionSlugs } from './special-diet-intake-dialog'

import { SpecialDietEditDialog } from './special-diet-edit-dialog'
import { SpecialDietOrderDialog } from './special-diet-order-dialog'

interface DietaryNotesCardProps {
  school: AdminSchool
  /**
   * The section's campus picker (`CampusScope`), rendered in this card's
   * header. Absent for a single-campus org — then the header renders exactly
   * as it did before per-card scoping.
   */
  scopeControl?: ReactNode
}

export function DietaryNotesCard({ school, scopeControl }: DietaryNotesCardProps) {
  const args = { organizationId: school.organizationId, locationId: school.id }
  const query = useSchoolAccommodationsQuery(args)
  const submit = useSubmitAccommodationMutation(args)
  const [open, setOpen] = useState(false)
  const noteOptions = useNoteOptionsQuery(school.id)

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>Special Diets</CardTitle>
        <div className="flex items-center gap-2">
          {scopeControl}
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={() => setOpen(true)}
            disabled={noteOptions.isPending || noteOptions.data?.specialDietsEnabled === false}>
            <Plus className="size-4" /> Add a note
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {noteOptions.data?.specialDietsEnabled === false && (
          <p className="text-sm text-muted-foreground">
            Special Diets is not enabled for this school. Contact your Ordo team.
          </p>
        )}
        {(query.error || noteOptions.error) && (
          <p role="alert" className="text-sm text-destructive">
            {(query.error ?? noteOptions.error)?.message ?? 'Unable to load Special Diets.'}
          </p>
        )}
        {query.isPending ? (
          // Block-for-block skeleton: two rows matching the list below.
          <div className="flex flex-col gap-3">
            {[0, 1].map((i) => (
              <div key={i} className="flex items-center justify-between gap-3 rounded-md border p-3">
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-56" />
                </div>
                <Skeleton className="h-5 w-24" />
              </div>
            ))}
          </div>
        ) : (query.data?.accommodations ?? []).length === 0 ? (
          <EmptyState
            icon={<FileText />}
            title="No notes on file"
            description="Add a note when a family brings one to the office. Ordo reviews it and sets up the student's menu."
          />
        ) : (
          (query.data?.accommodations ?? []).map((row) => {
            const restrictions = [...row.restrictedAllergens, ...row.dietaryNeeds].join(', ')
            return (
              <div key={row.id} className="flex items-start justify-between gap-3 rounded-md border p-3">
                <div className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">
                      {specialDietStudentLabel(row)}
                      {(row.revision ?? 1) > 1 ? ` · V${row.revision}` : ''}
                    </span>
                    <Badge variant={row.status === 'ACTIVE' ? 'default' : 'secondary'}>
                      {row.successor?.status === 'ACTIVE'
                        ? 'Previous version'
                        : accommodationStatusCopy(row.status, row.workflowStatus)}
                    </Badge>
                  </div>
                  <span className="text-muted-foreground text-xs">
                    {row.title}
                    {restrictions ? ` · ${restrictions}` : ''}
                  </span>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <SpecialDietEditDialog
                    accommodation={row}
                    school={school}
                    revisionsEnabled={query.data?.revisionsEnabled === true}
                  />
                  {row.status === 'ACTIVE' && row.studentUserId && (
                    <SpecialDietOrderDialog accommodation={row} locationId={school.id} />
                  )}
                </div>
              </div>
            )
          })
        )}
      </CardContent>

      {open ? (
        <AddNoteDialog
          open={open}
          onOpenChange={setOpen}
          school={school}
          pending={submit.isPending}
          error={submit.error?.message ?? null}
          onSubmit={async (values) => {
            if (!values.note) {
              return
            }
            await submit.mutateAsync({ ...values, note: values.note })
            setOpen(false)
          }}
        />
      ) : null}
    </Card>
  )
}
